import { useEffect, type ReactNode } from "react";
import { useAuth, useOrganization, useOrganizationList } from "@clerk/react";

/**
 * Ensures the signed-in user has an ACTIVE organisation before the app renders.
 *
 * Clerk only includes organisation claims in the session token when an org is
 * active in the session — being a member is not enough (a user can sit in their
 * personal context with no active org). AMSaaS users always operate within their
 * tenant org, so we activate the user's first membership automatically and hold
 * rendering until it's active, so tRPC calls always carry org context.
 */
export function EnsureActiveOrganization({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { isLoaded: orgLoaded, organization } = useOrganization();
  const {
    isLoaded: listLoaded,
    userMemberships,
    setActive,
  } = useOrganizationList({
    userMemberships: true,
  });

  const memberships = userMemberships?.data ?? [];
  const firstOrgId = memberships[0]?.organization.id;

  useEffect(() => {
    if (!isSignedIn || organization || !listLoaded || !setActive || !firstOrgId)
      return;
    void setActive({ organization: firstOrgId });
  }, [isSignedIn, organization, listLoaded, setActive, firstOrgId]);

  // Hold the app until org activation is fully resolved, so no query (e.g. auth.me)
  // ever fires with an org-less token. Two cases must wait:
  //   1. still resolving — auth/org/membership state not yet loaded for a signed-in user;
  //   2. activating      — memberships loaded, an org exists, but it isn't active yet.
  // Without (1) the membership list can still be loading (listLoaded === false), the
  // guard renders children, and auth.me fires before setActive runs → it 401s, and with
  // retry:false that error sticks → the user wrongly lands on "account not set up".
  const resolving = isSignedIn && authLoaded && (!orgLoaded || !listLoaded);
  const activating =
    isSignedIn &&
    authLoaded &&
    orgLoaded &&
    listLoaded &&
    !organization &&
    memberships.length > 0;
  // eslint-disable-next-line no-console -- temporary auth debug
  console.log("[auth-debug] EnsureActiveOrganization", {
    isSignedIn,
    authLoaded,
    orgLoaded,
    listLoaded,
    hasOrganization: Boolean(organization),
    activeOrgId: organization?.id ?? null,
    membershipCount: memberships.length,
    firstOrgId: firstOrgId ?? null,
    resolving,
    activating,
    rendering: !(resolving || activating),
  });
  if (resolving || activating) {
    return <div className="p-10 text-slate-500">Activating workspace…</div>;
  }

  return <>{children}</>;
}
